import React, {useCallback, useMemo, useState} from 'react';

import {useClientServerEffect} from 'coreHooks';
import useSSRStyle from 'coreHooks/useSSRStyle';
import createCtx from 'coreUtils/createCtx';

import Grid from 'brandComponents/Grid';

import {constructGridCssStyle} from '../../helpers';

import {transformResponsivenessData} from './helpers';
import {IGridContentfulColumnProps, TGridColumnAttrs} from './IGridContentfulColumn';

export const [useContentfulColumnCtx, ContentfulColumnProvider] = createCtx<{
	registerColumnClassName: (className: string) => void;
	unRegisterColumnClassName: (className: string) => void;
}>();

const GridContentfulColumn = (props: IGridContentfulColumnProps) => {
	const {addStyle, deleteStyle} = useSSRStyle();
	const [registeredClassNames, setRegisteredClassNames] = useState<string[]>([]);

	const registerColumnClassName = useCallback((className: string) => {
		if (registeredClassNames.includes(className)) {
			return;
		}

		setRegisteredClassNames([...registeredClassNames, className]);
	}, []);

	const unRegisterColumnClassName = useCallback((className: string) => {
		if (!registeredClassNames.includes(className)) {
			return;
		}

		setRegisteredClassNames(registeredClassNames.filter((registeredClassName) => {
			return registeredClassName !== className;
		}));
	}, []);

	const {attrs, classesKeys, classesStyles} = useMemo(() => {
		const columnAttrs: TGridColumnAttrs = transformResponsivenessData(props.responsiveness);
		// Edge-to-edge column

		if (props.edgeToEdge) {
			columnAttrs.edgeToEdge = props.edgeToEdge;
		}

		const classes = constructGridCssStyle(
			props.sys.id,
			props.spacing,
			props.alignment,
			props.skeleton,
			columnAttrs
		);

		const keys = Object.keys(classes || {});
		const styles = Object.entries(classes || {});

		return {attrs: columnAttrs, classesStyles: styles, classesKeys: keys};
	}, [props.sys.id, props.spacing, props.alignment, props.skeleton, props.responsiveness, props.edgeToEdge]);

	useClientServerEffect(() => {
		classesStyles.forEach(([className, style]) => addStyle(className, style as string));

		return () => classesKeys.forEach((key) => deleteStyle(key));
	}, []);

	return (
		<ContentfulColumnProvider value={{registerColumnClassName, unRegisterColumnClassName}}>
			<Grid.Column {...attrs} classNames={[...classesKeys, ...registeredClassNames]}>
				{props.children}
			</Grid.Column>
		</ContentfulColumnProvider>
	);
};

export default GridContentfulColumn;
